import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useThemeStore } from '../../../store/ConfigStore';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { useStore } from '@/store';
import { Table, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { Pagination } from "@/components/Pagination";
import type { LineData, SubAgentContributionRanking, InvitationCodeRanking } from '@/types/dataAnalysis';
import { fetchDataOverview } from '@/services/dataAnalysis';
import { MobileTableCard } from '@/components/MobileCards';
import type { Dayjs } from 'dayjs';
import Chart from '@/components/LineChart';
import dayjs from 'dayjs';

export const routeConfig: RouteConfig = {
    title: 'route.dataAnalysis.dataOverview',
    layout: true,
    sort: 0
};

const DataOverview = () => {
    const { t } = useLocale();
    const toast = useToast();

    // const [selectedProductLine, setSelectedProductLine] = useState(null);
    const [subAgentUID, setSubAgentUID] = useState('');
    const [selectedInvitationCode, setSelectedInvitationCode] = useState(null);
    const [transactionTime, setTransactionTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [loading, setLoading] = useState(false);
    const [contributionRankingData, setContributionRankingData] = useState<SubAgentContributionRanking[]>([]);
    const [codeRankingData, setCodeRankingData] = useState<InvitationCodeRanking[]>([]);
    const [lineData, setLineData] = useState<LineData>({
        commission: 0,
        totalTransactionVolume: 0,
        numberOfDeposits: 0,
        numberOfInvitees: 0,
        newUserTrend: [],
        commissionTrend: []
    });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5 });
    const [total, setTotal] = useState(0);

    // Helper to build params for API
    const buildDataParams = (subAgentUID: string, selectedInvitationCode: string | null, transactionTime: [Dayjs | null, Dayjs | null]) => {
        let params: any = {};
        if (subAgentUID) {
            params.user_id = subAgentUID;
        }
        if (selectedInvitationCode) {
            params.invitation_code = selectedInvitationCode;
        }
        if (transactionTime[0] && transactionTime[1]) {
            params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
        }
        return params;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = buildDataParams(subAgentUID, selectedInvitationCode, transactionTime);
            // params.page_no = pagination.current;
            // params.page_size = pagination.pageSize;
            const res = await fetchDataOverview(params);
            setLastUpdated(Math.floor(Date.now() / 1000)); // current time as last updated

            // Set line data
            setLineData({
                commission: Number(res.core_metrics.total_commission),
                totalTransactionVolume: Number(res.core_metrics.total_trade_volume),
                numberOfDeposits: res.core_metrics.total_depositors_count,
                numberOfInvitees: res.core_metrics.total_invitees_count,
                newUserTrend: res.new_user_trend?.map(item => ({
                    time: item.date,
                    value: item.value
                })) || [],
                commissionTrend: res.commission_trend?.map(item => ({
                    time: item.date,
                    value: item.value
                })) || []
            });

            // Set contribution ranking data
            setContributionRankingData(res.sub_agent_rank?.map((item, index) => ({
                rank: index + 1 + (pagination.current - 1) * pagination.pageSize,
                subAgentUID: item.id,
                commission: Number(item.commission),
                amount: Number(item.trade_volume)
            })) || []);

            // Set invitation code ranking data
            setCodeRankingData(res.invite_code_rank?.map((item, index) => ({
                rank: index + 1 + (pagination.current - 1) * pagination.pageSize,
                inviteCode: item.id,
                commission: Number(item.commission),
                registrationsCount: item.registration_count
            })) || []);

            // setTotal(res.total); // Assuming API returns total count for pagination
        } catch (error: any) {
            toast.error({
                message: error?.message || t('dataAnalysis.dataOverview.fetchError') || 'An error occurred while fetching data.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        try {
            setSubAgentUID('');
            setSelectedInvitationCode(null);
            setTransactionTime([null, null]);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            await fetchData();
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            const params = buildDataParams(subAgentUID, selectedInvitationCode, transactionTime);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            await fetchData();
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const queryFields: QueryField[] = [
        // {
        //     type: 'select',
        //     name: 'productLine',
        //     label: t('dataAnalysis.dataOverview.productLine'),
        //     value: selectedProductLine,
        //     onChange: setSelectedProductLine,
        //     options: [
        //         { value: null, label: t('dataAnalysis.dataOverview.all') },
        //         { value: 'futures', label: t('dataAnalysis.dataOverview.futures') },
        //         { value: 'spot', label: t('dataAnalysis.dataOverview.spot') }
        //     ]
        // },
        // {
        //     type: 'input',
        //     name: 'subAgentUID',
        //     label: t('dataAnalysis.dataOverview.subAgentUID'),
        //     value: subAgentUID,
        //     onChange: setSubAgentUID,
        //     placeholder: t('dataAnalysis.dataOverview.pleaseEnterPlaceholder')
        // },
        // {
        //     type: 'select',
        //     name: 'invitationCode',
        //     label: t('dataAnalysis.dataOverview.invitationCode'),
        //     value: selectedInvitationCode,
        //     onChange: setSelectedInvitationCode,
        //     options: [
        //         { value: null, label: t('dataAnalysis.dataOverview.all') },
        //         // get invite codes from API
        //         // { value: 'inviteCode1', label: t('dataAnalysis.dataOverview.inviteCode1') }
        //         // { value: 'inviteCode2', label: t('dataAnalysis.dataOverview.inviteCode2') }
        //     ]
        // },
        {
            type: 'dateRange',
            name: 'transactionTime',
            label: t('dataAnalysis.dataOverview.transactionTime'),
            value: transactionTime,
            onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
                setTransactionTime(dates ?? [null, null]);
            },
            placeholder: [t('dataAnalysis.dataOverview.startTime'), t('dataAnalysis.dataOverview.endTime')],
        }
    ];

    const SubAgentContributionRankingColumns: ColumnsType<SubAgentContributionRanking> = [
        {
            title: t('dataAnalysis.dataOverview.table.rank'),
            dataIndex: 'rank',
            key: 'rank',
            align: 'start',
            width: 60,
            ellipsis: true,
            render: (text) => formatNumber(text)
        },
        {
            title: t('dataAnalysis.dataOverview.table.subAgentUID'),
            dataIndex: 'subAgentUID',
            key: 'subAgentUID',
            align: 'start',
            width: 110,
            ellipsis: true,
            render: (text) => text || '--'
        },
        {
            title: t('dataAnalysis.dataOverview.table.contributionCommission'),
            dataIndex: 'contributionCommission',
            key: 'contributionCommission',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        },
        {
            title: t('dataAnalysis.dataOverview.table.transactionAmount'),
            dataIndex: 'transactionAmount',
            key: 'transactionAmount',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        }
    ];

    const InvitationCodeContributionColumns: ColumnsType<InvitationCodeRanking> = [
        {
            title: t('dataAnalysis.dataOverview.table.rank'),
            dataIndex: 'rank',
            key: 'rank',
            align: 'start',
            width: 60,
            ellipsis: true,
            render: (text) => formatNumber(text)
        },
        {
            title: t('dataAnalysis.dataOverview.table.invitationCode'),
            dataIndex: 'invitationCode',
            key: 'invitationCode',
            align: 'start',
            width: 110,
            ellipsis: true,
            render: (text) => text || '--'
        },
        {
            title: t('dataAnalysis.dataOverview.table.contributionCommission'),
            dataIndex: 'contributionCommission',
            key: 'contributionCommission',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        },
        {
            title: t('dataAnalysis.dataOverview.table.registrationsCount'), // number of registrations
            dataIndex: 'registrationsCount',
            key: 'registrationsCount',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        }
    ];

    const mobileSubAgentColumns = SubAgentContributionRankingColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    const mobileInvitationCodeColumns = InvitationCodeContributionColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    // Fetch data whenever filters or pagination change
    useEffect(() => {
        fetchData();
    }, [subAgentUID, selectedInvitationCode, transactionTime, pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('dataAnalysis.dataOverview.title')}
                subtitle={t('dataAnalysis.dataOverview.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                gridCols="grid-cols-2 md:grid-cols-4"
            />

            <div className="mt-6 w-full gap-6">
                <div className="flex flex-col gap-4 p-6 bg-card-background dark:bg-card-background rounded-lg items-start">
                    <div>
                        <div className="text-sel-normal dark:text-dark-sel-normal text-lg font-medium font-['Avenir_Next'] leading-tight">
                            {t('dataAnalysis.dataOverview.line.title')}
                        </div>
                    </div>
                    <div className="w-full grid grid-col-2 md:grid-cols-4 gap-2">
                        <div className="flex flex-col p-3 gap-3 border border-normal dark:border-dark-normal rounded-lg">
                            <span className="text-normal dark:text-dark-normal text-sm font-medium font-inter leading-none">
                                {t('dataAnalysis.dataOverview.line.commission')}
                            </span>
                            <span className="text-sel-normal dark:text-dark-sel-normal text-xl font-semibold font-['Avenir_Next'] leading-normal">
                                {formatNumber(lineData.commission)}
                            </span>
                        </div>
                        <div className="flex flex-col p-3 gap-3 border border-normal dark:border-dark-normal rounded-lg">
                            <span className="text-normal dark:text-dark-normal text-sm font-medium font-inter leading-none">
                                {t('dataAnalysis.dataOverview.line.totalTransactionVolume')}
                            </span>
                            <span className="text-sel-normal dark:text-dark-sel-normal text-xl font-semibold font-['Avenir_Next'] leading-normal">
                                {formatNumber(lineData.totalTransactionVolume)}
                            </span>
                        </div>
                        <div className="flex flex-col p-3 gap-3 border border-normal dark:border-dark-normal rounded-lg">
                            <span className="text-normal dark:text-dark-normal text-sm font-medium font-inter leading-none">
                                {t('dataAnalysis.dataOverview.line.numberOfInvitees')}
                            </span>
                            <span className="text-sel-normal dark:text-dark-sel-normal text-xl font-semibold font-['Avenir_Next'] leading-normal">
                                {formatNumber(lineData.numberOfInvitees)}
                            </span>
                        </div>
                        <div className="flex flex-col p-3 gap-3 border border-normal dark:border-dark-normal rounded-lg">
                            <span className="text-normal dark:text-dark-normal text-sm font-medium font-inter leading-none">
                                {t('dataAnalysis.dataOverview.line.numberOfDeposits')}
                            </span>
                            <span className="text-sel-normal dark:text-dark-sel-normal text-xl font-semibold font-['Avenir_Next'] leading-normal">
                                {formatNumber(lineData.numberOfDeposits)}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[325px] gap-4 p-6 border border-normal dark:border-dark-normal rounded-lg">
                        <div className="text-base font-medium font-['Avenir_Next'] leading-none">{t('dataAnalysis.dataOverview.line.newUserTrend')}</div>
                        <div className="flex w-full h-full md:p-6">
                            <Chart height={'100%'} data={lineData.newUserTrend} />
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[325px] gap-4 p-6 border border-normal dark:border-dark-normal rounded-lg">
                        <div className="text-base font-medium font-['Avenir_Next'] leading-none">{t('dataAnalysis.dataOverview.line.commissionTrend')}</div>
                        <div className="flex w-full h-full md:p-6">
                            <Chart height={'100%'} data={lineData.commissionTrend} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Table Container with Custom Layout */}
                <div className="mt-6 bg-card-background dark:bg-card-background h-[485px] rounded-lg p-6 overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-sel-normal dark:text-dark-sel-normal text-lg font-medium font-['Avenir_Next'] leading-tight">
                            {t('dataAnalysis.dataOverview.table.subAgentContributionRanking')}
                        </div>
                    </div>
                    <div className="hidden md:block h-full overflow-hidden overflow-y-auto overflow-x-auto">
                        <Table
                            dataSource={contributionRankingData}
                            columns={SubAgentContributionRankingColumns}
                            rowKey="date"
                            pagination={false}
                            loading={loading}
                            size="small"
                            // scroll={{ x: 850 }}
                            className="custom-table"
                            showHeader={true}
                            locale={{ emptyText: '' }}
                            components={{
                                body: {
                                    wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                                }
                            }}
                        />
                    </div>
                    <div className="block md:hidden">
                        {contributionRankingData.map((row, idx) => (
                            <MobileTableCard
                                key={row.rank}
                                record={row}
                                columns={mobileSubAgentColumns}
                                t={t}
                            />
                        ))}
                    </div>
                    {/* <div className="mt-4 flex justify-end">
                        <Pagination
                            currentPage={pagination.current}
                            totalPages={Math.ceil(total / pagination.pageSize)}
                            onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                            pageSize={pagination.pageSize}
                            setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                            pageSizes={[10, 20, 50, 100]}
                            isPageSizeVisible={true}
                            disabled={loading}
                        />
                    </div> */}
                </div>

                {/* Table Container with Custom Layout */}
                <div className="mt-6 bg-card-background dark:bg-card-background h-[485px] rounded-lg p-6 overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-sel-normal dark:text-dark-sel-normal text-lg font-medium font-['Avenir_Next'] leading-tight">
                            {t('dataAnalysis.dataOverview.table.invitationCodeContributionRanking')}
                        </div>
                    </div>
                    <div className="hidden md:block h-full overflow-hidden overflow-y-auto overflow-x-auto">
                        <Table
                            dataSource={codeRankingData}
                            columns={InvitationCodeContributionColumns}
                            rowKey="date"
                            pagination={false}
                            loading={loading}
                            size="small"
                            // scroll={{ x: 850 }}
                            className="custom-table"
                            showHeader={true}
                            locale={{ emptyText: '' }}
                            components={{
                                body: {
                                    wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                                }
                            }}
                        />
                    </div>
                    <div className="block md:hidden">
                        {codeRankingData.map((row, idx) => (
                            <MobileTableCard
                                key={row.rank}
                                record={row}
                                columns={mobileInvitationCodeColumns}
                                t={t}
                            />
                        ))}
                    </div>
                    {/* <div className="mt-4 flex justify-end">
                        <Pagination
                            currentPage={pagination.current}
                            totalPages={Math.ceil(total / pagination.pageSize)}
                            onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                            pageSize={pagination.pageSize}
                            setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                            pageSizes={[10, 20, 50, 100]}
                            isPageSizeVisible={true}
                            disabled={loading}
                        />
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default DataOverview;